<template>
  <WdsMenu
    z-index="50"
    placement="bottom-end"
    manual-trigger
    :global="tableGlobals"
    @menu:open="load"
  >
    <template #trigger="{ toggle }">
      <WdsButtonGhost :data-test="`tc-settings-${index}`" @click="toggle">
        <WdsIcon name="overflow-menu-horizontal" :size="16" />
      </WdsButtonGhost>
    </template>

    <div v-if="permissions && !loading">
      <TrustedContactApprove
        v-if="permissions.viewerCanApprove"
        :trusted-contact-id="permissions.id"
        @approved="$emit('approved')"
        @error="$emit('approve-error', $event)"
      >
        <template #default="{ loading: loadingApproval }">
          <WdsMenuOption :disabled="loadingApproval" :data-test="`tc-approve-${index}`">
            <WdsIcon :size="16" name="checkmark" />
            {{ $t('Approve') }}
          </WdsMenuOption>
        </template>
      </TrustedContactApprove>

      <a
        v-if="permissions.worker.viewerCanContact"
        target="_blank"
        :href="
          laravelNamedRoute(namedRoutes.frontend.user.profile.invite, {
            freelancer: permissions.worker.id,
          })
        "
      >
        <WdsMenuOption :data-test="`tc-message-${index}`">
          <WdsIcon :size="16" name="chat" />
          {{ $t('Message') }}
        </WdsMenuOption>
      </a>

      <WdsMenuOption
        v-if="permissions.viewerCanUpdate"
        :data-test="`tc-edit-${index}`"
        @click="$emit('edit', permissions)"
      >
        <WdsIcon :size="16" name="edit" />
        {{ $t('Edit contact') }}
      </WdsMenuOption>

      <WdsMenuOption
        v-if="permissions.viewerCanDelete"
        :data-test="`tc-delete-${index}`"
        @click="$emit('delete', permissions)"
      >
        <WdsIcon :size="16" name="trash-can" />
        {{ $t('Delete') }}
      </WdsMenuOption>

      <WdsMenuOption
        v-if="permissions.viewerCanDelete"
        theme="DESTRUCTIVE"
        :data-test="`tc-block-${index}`"
        @click="$emit('block', permissions)"
      >
        <WdsIcon :size="16" name="error" />
        {{ $t('Decline & block') }}
      </WdsMenuOption>
    </div>
    <div v-else-if="loading" class="tw-px-4 tw-py-2">
      <WdsSpinner indeterminate />
    </div>
  </WdsMenu>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsSpinner,
} from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import TrustedContactApprove from '@/components/Contacts/CompanyFreelancer/TrustedContactApprove.vue'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const AppliedContactPermissionsRequest = graphql(`
  fragment AppliedContactPermissionsRequest on TrustedContact {
    id
    viewerCanApprove(account: $companyId)
    viewerCanUpdate(account: $companyId)
    viewerCanDelete(account: $companyId)
    worker {
      id
      viewerCanContact(account: $companyId)
    }
  }
`)

defineEmits<{
  approved: []
  'approve-error': [errors: string[]]
  edit: [row: any]
  delete: [row: any]
  block: [row: any]
}>()

const props = defineProps<{ row: { id: string }; index: number }>()

const { result, loading, load } = useLazyQuery(
  graphql(`
    query AppliedContactActionsPermissions($id: ID!, $companyId: ID!) {
      trustedContact(id: $id) {
        ...AppliedContactPermissionsRequest
      }
    }
  `),
  () => ({ id: props.row.id, companyId: currentAccount.globalId }),
  { clientId: 'publicApiCustomFields' },
)

const permissions = computed(() =>
  useFragment(AppliedContactPermissionsRequest, result.value?.trustedContact),
)
</script>
